import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReturnButtons from '../../components/core/ReturnButtons';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { loadScript } from '../../../services/dynamic-script';
import {
  FormControlLabel,
  RadioGroup,
  Box,
  Card,
  Grid,
  IconButton,
  Slider,
  Button,
  Typography,
  Radio,
  TextField,
  Tooltip,
  Paper,
  Collapse,
  CircularProgress,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { ExpandLess, ExpandMore, LayersOutlined } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import SimpleInfoDialog from '../../components/dialog/SimpleInfoDialog';
import { Actions as CSqueeze } from '../../../store/ducks/csqueeze';
import { landCoversIntersectionsTurf, extractShoreLineTurf } from '../../../algorithms/csqueeze';
import { exportGeoJson } from '../../../algorithms/csqueeze/geoJsonExport';
import { exportSession, useLocalStorage } from '../../../common/utils.js';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import * as turf from '@turf/turf';
import TransectResult from '../../components/csqueeze/_TransectResult';
import Export from '../../../services/export';
import { periodFilenamePart, timer } from '../../../algorithms/utils';
import { GoogleMap } from '../../components';
import { Actions as Map } from '../../../store/ducks/map';
import AnalysisSteps from '../../components/steps/AnalysisSteps';
import { parseCsv } from '../../../common/utils';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_PANEL_GAP,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_SIDE_COLUMN_WIDTH,
  OVERLAY_COLUMN_LAYOUT,
  fitBoundsWithOverlayPadding,
  fitCoordinatesWithOverlayPadding,
} from '../Shoreline/theme.js';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import { buildSessionExport, readPendingBaseline } from '../../../algorithms/csqueeze/sessionExport.js';
import { buildLandCoverReport, REPORT_MODULE } from '../../../algorithms/csqueeze/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';
import { aoiPaths } from '../../../common/aoi';

const getPeriodSuffix = (key) => {
  const str = String(key);
  const suffixIndex = str.indexOf('-');
  if (suffixIndex === -1) return -Infinity;
  const suffix = parseInt(str.slice(suffixIndex + 1), 10);
  return Number.isFinite(suffix) ? suffix : -Infinity;
};

const comparePeriodKeys = (a, b) => {
  const baseA = parseInt(String(a).slice(0, 4), 10);
  const baseB = parseInt(String(b).slice(0, 4), 10);
  if (baseA !== baseB) return baseA - baseB;
  return getPeriodSuffix(b) - getPeriodSuffix(a);
};

/**
 * @function ProcessingPage
 * @description CSqueeze processing page. Uses the AnalysisSteps component for the
 * baseline drawing and transect configuration workflow, then runs the per-transect
 * land-cover intersection analysis (coastal squeeze), with results exportable as CSV
 * and shapefile.
 * @see {@link AnalysisSteps} - manages the step flow (draw baseline, configure transects, analyze).
 * @see {@link generateTransects} - creates transects along the drawn baseline using turf.js.
 * @see {@link startCalc} - runs the per-transect land-cover intersection analysis.
 * @see {@link TransectResult} - renders each transect's result.
 * **Called by:
 * @see {MainPage} - via the `/csqueeze/result` Route (imported as ProcessingCSqueezePage).
 * @memberof module:Csqueeze
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingPage = () => {
  const dispatch = useDispatch();
  const [t, i18n] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const classes = useStyles();
  const mapRef = useRef();
  const drawingManager = useRef();
  const ROI = useRef();
  const boundsRef = useRef();
  const transectMarker = useRef();
  const baselineRef = useRef();
  const baselineBufferRef = useRef([]);
  const labelsVisibleRef = useRef(false);
  const [images] = useSelector((state) => state.csqueeze.studyData);
  const classLandCoversVectors = useSelector((state) => state.csqueeze.classLandCoversVectors);
  const waterLandCoverVectors = useSelector((state) => state.csqueeze.waterLandCoverVectors);
  const csqueezeYears = useSelector((state) => state.csqueeze.years);
  const csqueezeDates = useSelector((state) => state.csqueeze.dates);
  const csqueezeMissions = useSelector((state) => state.csqueeze.missions);
  const acquisitionAvailableDates = useSelector((state) => state.acquisition.availableDates);
  const imageList = useSelector((state) => state.csqueeze.imageList);
  const periodLabels = useSelector((state) => state.csqueeze.periodLabels);
  const periodLabel = (key) => periodLabels[key] ?? key;
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const [finished, setFinished] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [baselineBufferReady, setBaselineBufferReady] = useState(false);

  const [mapReady, setMapReady] = useState(false);

  const [baselineCoords, setBaselineCoords] = useState(null);

  const [transectsSqueeze, setTransectsSqueeze] = useState({});

  const restoredAnalysisRef = useRef(false);
  const [transectsData, setTransectsData] = useState([]);
  const [transectsResults, setTransectsResults] = useState(false);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [transectsScale, setTransectsScale] = useState(1);
  const [transectsCsv, setTransectsCsv] = useState('');
  const [transectsCalcs, setTransectsCalcs] = useState({});
  const [showHelpDialogVisibility, setShowHelpDialogVisibility] = useState(false);
  const [showHelpDialogCSV, setShowHelpDialogCSV] = useState(false);
  const [showHelpDialogShp, setShowHelpDialogShp] = useState(false);
  const [selectedTransect, setSelectedTransect] = useState(1);
  const [previsao, setPrevisao] = useState(10);
  const [mapType, setMapType] = useLocalStorage('csqueezeMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useState(false);
  const [layersPanelOpen, setLayersPanelOpen] = useState(true);
  const [years, setYears] = useState([]);
  const [opacity, setOpacity] = useState({
    0: { overlay: 0, raster: 1 },
    1: { overlay: 0, raster: 0 },
    2: { transects: true, baseline: true },
  });
  const [calcMessage, setCalcMessage] = useState(t('forms.csqueeze.result.loading.preparing'));
  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const csqueezeTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );
  const themeModeRef = useRef(shorelineThemeMode);

  /**
   * @function applyMapThemeStyle
   * @description Recomputes the live map's `styles` array from the module theme mode, the
   * map's current base layer and the "Marcadores" toggle. Dark styling applies only when the
   * mode is dark AND the base layer is roadmap/terrain — satellite/hybrid imagery is never
   * recolored. Reads both inputs through refs because this also runs from the map's own
   * `maptypeid_changed` listener, which is registered once and would otherwise close over the
   * values they had at mount.
   * @see {@link buildMapStyles}
   * **Called by:
   * @see {ProcessingPage} - on theme-mode / labels change, and via the map's maptypeid_changed listener.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const applyMapThemeStyle = () => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({
      styles: buildMapStyles({
        themeMode: themeModeRef.current,
        mapType: mapRef.current.getMapTypeId(),
        labelsVisible: labelsVisibleRef.current,
      }),
    });
  };

  /**
   * @function handleMapTypeChange
   * @description Switches the live Google Maps instance's base layer from the compact "Mapa de
   * fundo" panel, without CSqueeze's old native map-type control (now disabled).
   * @param {String} type - A Google Maps `mapTypeId` (`'roadmap'`, `'satellite'`, `'hybrid'`, or
   * `'terrain'`).
   * **Called by:
   * @see {ProcessingPage} - via `BackgroundMapPanel`'s `onMapTypeChange`.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    themeModeRef.current = shorelineThemeMode;
    labelsVisibleRef.current = labelsVisible;
    applyMapThemeStyle();
  }, [shorelineThemeMode, labelsVisible]);

  /**
   * @function displayROI
   * @description Responsible for showing on screen the region of interest (ROI)
   * **Called by:
   * @see {ProcessingPage} - via the GoogleMap's onLoad callback.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const displayROI = () => {
    if (!coordinates) return;
    dispatch(Map.centralizeMap(coordinates));
    const formatedCoordinates = aoiPaths(coordinates);
    ROI.current = new window.google.maps.Polygon({
      paths: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    ROI.current.setMap(mapRef.current);
    boundsRef.current = new window.google.maps.LatLngBounds();
    ROI.current
      .getPaths()
      .getArray()
      .flatMap((path) => path.getArray())
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        boundsRef.current.extend(latLng);
      });
    fitBoundsWithOverlayPadding(mapRef.current, boundsRef.current);
  };

  /**
   * Redraws and reframes the area when it arrives after the map has already loaded. A session
   * import sets the coordinates at that point, and without this the area was drawn but never
   * framed, leaving the user looking at the world while the analysis sat somewhere off screen.
   * @see {@link displayROI}
   * @memberof module:Csqueeze
   */
  useEffect(() => {
    if (!mapRef.current || !coordinates?.length) return;
    ROI.current?.setMap(null);
    displayROI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);


  useEffect(() => {
    loadScript('google-charts', 'https:www.gstatic.com/charts/loader.js', () => {
      window.google.charts.load('current', {
        packages: ['corechart', 'bar', 'table'],
      });
    });
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    const values = Object.values(images);
    if (values.length > 0) {
      let tempYears = [];
      values.forEach((image, i) => {
        if (image.overlay !== undefined && image.classification !== undefined) {
          image.overlay.setMap(mapRef.current);
          image.overlay.setOpacity(0);
          image.classification.setMap(mapRef.current);
          image.classification.setOpacity(i === values.length - 1 ? 1 : 0);
        } else {
          console.error('Image without overlay', image, i);
        }
        tempYears.push(image.date);
      });
      setYears(tempYears);
    }
  }, [images, mapReady]);

  useEffect(() => {
    if (Object.keys(transectsResults).length <= 0 || years.length < 2) return;
    let tempTransectsCalcs = {};
    const periodKeys = [...years].sort(comparePeriodKeys);
    const baseYears = periodKeys.map((key) => parseInt(key.slice(0, 4), 10));
    const firstYear = baseYears[0];
    const lastYear = baseYears[baseYears.length - 1];
    const yearsDifference = Math.max(lastYear - firstYear, 1);
    let numberTransects = Object.values(transectsResults)[0].length;
    let flattenResults = Object.values(transectsResults).flat();
    let csvData = `${t('forms.csqueeze.result.labels.id')};`;
    let csvYears = [...periodKeys.map((key) => periodLabel(String(key))), String(lastYear + Number(previsao))];
    for (let csvYear of csvYears) {
      for (let landCover of flattenResults[0].landCoversIntersections) {
        csvData += `${csvYear}_${t(`forms.csqueeze.result.labels.${landCover.label}`)};`;
      }
    }
    csvData = csvData.slice(0, -1) + `\n`;
    const squeezeByTransect = {};
    for (let transect = 1; transect <= numberTransects; transect++) {
      if (flattenResults[transect] === null) continue;
      let centerCoords,
        landcoversOutPut = {},
        rate = {
          water: null,
          human: null,
          mangroove: null,
          vegetation: null,
        },
        extra = {
          humanLastYear: null,
          mangrooveLastYear: null,
        },
        outputtext = '';
      const mapObject = transectsObjects.filter((object) => object.transect === transect)[0];
      if (!mapObject) continue;
      const dataTransect = flattenResults
        .filter((result) => result.id === transect)
        .sort((a, b) => comparePeriodKeys(b.year, a.year));
      dataTransect.forEach((data, index) => {
        centerCoords = data.center;
        data.landCoversIntersections.forEach((land) => {
          if (landcoversOutPut[land.label] === undefined)
            landcoversOutPut[land.label] = {
              id: land.label,
              landcover: land.classe,
              years: [],
              completeLine: data.completeLine,
            };
          let length = Number(land.lengthSide.reduce((a, b) => a + b).toFixed(2));
          let shoreLineDistance = Number(land.shoreLineDistance.sort()[1].toFixed(2));
          let baseLineDistance = Number(land.baseLineDistance.sort()[0].toFixed(2));
          switch (land.label) {
            case 0:
              if (index === 0) extra.mangrooveLastYear = length;
              rate.mangroove = rate.mangroove === null ? length : rate.mangroove - length;
              break;
            case 1:
              rate.vegetation = rate.vegetation === null ? length : rate.vegetation - length;
              break;
            case 2:
              rate.water = rate.water === null ? baseLineDistance : rate.water - baseLineDistance;
              break;
            case 3:
              if (index === 0) extra.humanLastYear = shoreLineDistance;
              rate.human = rate.human === null ? shoreLineDistance : rate.human - shoreLineDistance;
              break;
            default:
              break;
          }
          landcoversOutPut[land.label].years.push({
            year: data.year,
            length,
            completeLine: data.completeLine,
          });
        });
      });
      let rowsTemp = {};
      Object.values(landcoversOutPut)
        .sort((l1, l2) => l2.id - l1.id)
        .forEach((land) => {
          if (land.id === 2) return;
          const id = land.id === 0 ? 2 : land.id === 1 ? 1 : land.id === 3 ? 0 : land.id === 4 ? 4 : null;
          land.years
            .sort((a, b) => a.year - b.year)
            .forEach((year) => {
              if (rowsTemp[year.year] === undefined) rowsTemp[year.year] = [0.0, 0.0, 0.0, 0.0, 0.0];
              rowsTemp[year.year][id] = parseFloat(year.length);
            });
          if (rowsTemp[lastYear + previsao] === undefined) rowsTemp[lastYear + previsao] = [0.0, 0.0, 0.0, 0.0, 0.0];
          let tempRateNumber = land.years
            .sort((a, b) => comparePeriodKeys(a.year, b.year))
            .reduce((a, b) => b.length - a.length);
          let rate = tempRateNumber / yearsDifference;
          let yearLength = land.years[1] !== undefined ? land.years[1].length : 0;
          let numberFuture = (rate * previsao + yearLength).toFixed(2);
          rowsTemp[lastYear + previsao][id] = parseFloat(numberFuture > 0 ? numberFuture : 0);
        });

      Object.values(landcoversOutPut)
        .sort((l1, l2) => l2.id - l1.id)
        .forEach((land) => {
          if (land.id !== 2) return;
          let id = 3;
          land.years
            .sort((a, b) => a.year - b.year)
            .forEach((year) => {
              if (rowsTemp[year.year] === undefined) rowsTemp[year.year] = [0.0, 0.0, 0.0, 0.0, 0.0];
              rowsTemp[year.year][id] = parseFloat(year.length);
            });
          if (rowsTemp[lastYear + previsao] === undefined) rowsTemp[lastYear + previsao] = [0.0, 0.0, 0.0, 0.0, 0.0];
          let tempRateNumber = land.years
            .sort((a, b) => comparePeriodKeys(a.year, b.year))
            .reduce((a, b) => b.length - a.length);
          let rate = tempRateNumber / yearsDifference;
          let yearLength = land.years[1] !== undefined ? land.years[1].length : 0;
          let numberFuture = (rate * previsao + yearLength).toFixed(2);
          rowsTemp[lastYear + previsao][id] = parseFloat(numberFuture > 0 ? numberFuture : 0);
        });
      const originalTransectLength = rowsTemp[lastYear].reduce((a, b) => a + b, 0);
      const transectLength = rowsTemp[lastYear].reduce((a, b) => a + b, 0) + yearsDifference;
      if (transectLength > originalTransectLength) {
        let availableArea = originalTransectLength - rowsTemp[lastYear][3];
        if (rowsTemp[lastYear + previsao][0] > availableArea) {
          rowsTemp[lastYear + previsao][0] = availableArea;
        }
      }
      let rowsInfos = [...periodKeys, String(lastYear + Number(previsao))]
        .map((key) => [periodLabel(key), rowsTemp[key]])
        .filter(([, row]) => row !== undefined);
      let rows = [
        [
          t('forms.csqueeze.year'),
          t('forms.csqueeze.landcovers.human'),
          t('forms.csqueeze.landcovers.vegetation'),
          t('forms.csqueeze.landcovers.mangrove'),
          t('forms.csqueeze.landcovers.water'),
          t('forms.csqueeze.landcovers.beach'),
        ],
      ];
      for (let x = 0; x < rowsInfos.length; x++) {
        let rowInfo = rowsInfos[x];
        rows.push([rowInfo[0], ...rowInfo[1]]);
      }
      rows[3][3] = rows[2][3];
      let futureVegetation = rows[2][2] - (rows[3][1] - rows[2][1]);
      rows[3][2] = futureVegetation > 0 ? futureVegetation : 0;
      if (futureVegetation < 0) {
        let finalMangue = rows[3][3] + futureVegetation;
        rows[3][3] = finalMangue > 0 ? finalMangue : 0;
      }
      let diffWater = rows[2][4] - rows[1][4];
      let rateWater = diffWater / yearsDifference;
      let waterResult = rows[2][4] + rateWater * previsao;
      rows[3][4] = waterResult > 0 ? waterResult : 0;
      if (waterResult > rows[2][4]) {
        let diffMangue = rows[3][3] - (waterResult - rows[2][4]);
        rows[3][3] = diffMangue > 0 ? diffMangue : 0;
      }
      let squeezeClass = 'insufficient_mangrove';
      let squeezePercent = null;
      let squeezeColor = 'gray';

      if (rows[2][3] > 0) {
        let diffMangrove = rows[3][3] - rows[2][3];
        let divMangrove = rows[3][3] / rows[2][3];
        if (diffMangrove < 0) {
          let squeeze = (divMangrove - 1) * 100;
          const threshold = -100;
          const epsilon = 1e-6;
          if (Math.abs(squeeze - threshold) < epsilon || squeeze <= -100) {
            outputtext = t('forms.csqueeze.result.output.fullSqueeze');
            squeezeClass = 'full_squeeze';
            squeezePercent = 100;
            squeezeColor = 'red';
            mapObject.setOptions({ strokeColor: 'red' });
          } else {
            outputtext = t('forms.csqueeze.result.output.squeeze').replace('{number}', Math.abs(squeeze.toFixed(2)));
            squeezeClass = 'moderate_squeeze';
            squeezePercent = Number(Math.abs(squeeze).toFixed(2));
            squeezeColor = 'orange';
            mapObject.setOptions({ strokeColor: 'orange' });
          }
        } else {
          outputtext = t('forms.csqueeze.result.output.noSqueeze');
          squeezeClass = 'stable_mangrove';
          squeezePercent = 0;
          squeezeColor = 'green';
          mapObject.setOptions({ strokeColor: 'green' });
        }
      } else {
        outputtext = t('forms.csqueeze.result.output.noMangrove');
        mapObject.setOptions({ strokeColor: 'gray' });
      }

      squeezeByTransect[transect] = {
        squeeze_class: squeezeClass,
        squeeze_percent: squeezePercent,
        squeeze_label: outputtext,
        squeeze_color: squeezeColor,
        forecast_years: previsao,
      };
      let yearData = `${transect};`;
      for (let r = 1; r < rows.length; r++) {
        for (let c = 1; c < rows[r].length; c++) {
          yearData += `${rows[r][c].toFixed(2)};`;
        }
      }
      csvData += yearData.slice(0, -1) + `\n`;
      for (let r = 0; r < rows.length; r++) {
        if (tempTransectsCalcs[transect] === undefined) tempTransectsCalcs[transect] = [];
        tempTransectsCalcs[transect].push(rows[r]);
      }
      setTransectsCalcs({ ...tempTransectsCalcs });
      window.google.maps.event.clearInstanceListeners(mapObject.polyline);
      window.google.maps.event.addListener(mapObject.polyline, 'mouseover', () => {
        mapObject.setOptions({ strokeWeight: 9 });
      });
      window.google.maps.event.addListener(mapObject.polyline, 'mouseout', () => {
        mapObject.setOptions({ strokeWeight: 3 });
      });
      window.google.maps.event.addListener(mapObject.polyline, 'click', () => {
        setSelectedTransect(transect);
      });
    }
    setTransectsCsv(csvData);
    setTransectsSqueeze(squeezeByTransect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transectsResults, previsao, transectsObjects, i18n.language]);

  /**
   * @description Runs the per-transect, per-year land-cover intersection analysis once both
   * years' classified land-cover/water vectors are available. Uses the baselineBuffer to
   * extract each year's shoreline from the water vector, then computes each transect's
   * land-cover intersections for that year, updating the progress message as it goes.
   * @see {@link extractShoreLineTurf}
   * @see {@link landCoversIntersectionsTurf}
   * @memberof module:Csqueeze
   */
  useEffect(() => {
    if (!analyzing || !baselineBufferReady) return;
    if (Object.values(classLandCoversVectors).length !== 2 || waterLandCoverVectors.length !== 2) return;
    let results = {};
    let yearsArray = Object.values(years);
    (async () => {
      for (let i = 0; i < yearsArray.length; i++) {
        const year = yearsArray[i];
        results[year] = [];
        let yearClass = classLandCoversVectors[year];
        let waterGeometry = waterLandCoverVectors.filter((water) => water.year === year)[0]?.geometry;
        if (!waterGeometry) {
          throw new Error(`Water land-cover geometry missing for year ${year}.`);
        }
        let shoreLine = extractShoreLineTurf(baselineBufferRef.current, waterGeometry);
        setCalcMessage(t('forms.csqueeze.result.output.organizeYear').replace('{year}', periodLabel(year)));
        let messageTranslate = t('forms.csqueeze.result.output.calculatingTransect');
        await timer(10);
        for (let j = 0; j < transectsData.length; j++) {
          const transect = transectsData[j];
          let message = `${periodLabel(year)}: ${messageTranslate} ${transect.transect}/${transectsData.length}`;
          setCalcMessage(message);
          let landCoversResult = landCoversIntersectionsTurf(transect, yearClass, shoreLine, year);
          await timer(10);
          results[year].push(landCoversResult);
        }
        setCalcMessage(t('forms.csqueeze.result.output.calculatingComplete').replace('{year}', periodLabel(year)));
      }
      setTransectsResults(results);
      setFinished(true);
      setCalcMessage(t('forms.csqueeze.result.output.ready'));
      setSelectedTransect(2);
      setTimeout(() => {
        setSelectedTransect(1);
        setAnalyzing(false);
      }, 2000);
    })();
  }, [analyzing, baselineBufferReady, classLandCoversVectors, waterLandCoverVectors]);

  useEffect(() => {
    transectsObjects.forEach((obj) => {
      if (obj.transect === parseInt(selectedTransect)) {
        const bounds = new window.google.maps.LatLngBounds();
        obj
          .getPath()
          .getArray()
          .forEach((coord) => {
            let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
            bounds.extend(latLng);
          });
        const center = bounds.getCenter();
        transectMarker.current.setMap(mapRef.current);
        transectMarker.current.setLabel(`${selectedTransect}`);
        transectMarker.current.setPosition(center);
        transectMarker.current.setVisible(true);
        obj.setOptions({ strokeWeight: 9 });
      } else obj.setOptions({ strokeWeight: 3 });
    });
  }, [selectedTransect]);

  /**
   * @function startCalc
   * @description Kicks off the coastal squeeze analysis. Generates transects along the
   * baseline using turf.js, creates map polylines for them, computes the baseline buffer,
   * and dispatches the vectorization request if the classified land-cover vectors aren't
   * cached yet.
   * @see {@link generateTransects}
   * @see {@link timer}
   * **Called by:
   * @see {AnalysisSteps} - via the onAnalysisPerform callback when the ANALYSIS_DONE step is reached.
   * @memberof module:Csqueeze
   * @returns {Promise<void>}
   */
  /**
   * @description Brings a finished analysis back from an imported session.
   * The import leaves the baseline, the transects and the measurements taken along them waiting in
   * session storage; this puts them back on the map and into the page's state. Only the
   * measurements are restored, not the tables drawn from them: the effect that builds those runs
   * on `transectsResults` and does the whole job - the per-year table, the verdict, the colour of
   * each transect and the CSV - so handing it the measurements leaves the page exactly as a
   * completed run does, and the forecast selector keeps working because it can still recompute.
   * Waits for the years, which the imagery effect sets: the table is laid out per year, and the
   * derivation cannot run without them. Reading the pending payload also clears it, so it must not
   * be read before it can be used.
   * @memberof module:Csqueeze
   */
  useEffect(() => {
    if (!mapReady || !years.length || restoredAnalysisRef.current) return;

    const pending = readPendingBaseline();
    if (!pending?.baseLineCoords?.length) return;
    restoredAnalysisRef.current = true;

    const baselineOverlay = new window.google.maps.Polyline({
      path: pending.baseLineCoords.map(([lng, lat]) => ({ lat, lng })),
      geodesic: true,
      strokeColor: 'yellow',
      strokeOpacity: 1,
      strokeWeight: 3,
      map: mapRef.current,
    });
    baselineRef.current = { overlay: baselineOverlay };
    setBaselineCoords(pending.baseLineCoords);
    if (pending.transectsScale) setTransectsScale(pending.transectsScale);
    if (pending.forecastYears) setPrevisao(pending.forecastYears);

    if (pending.transectsData?.length) {
      setTransectsData(pending.transectsData);
      setTransectsObjects(createTransectObjects(pending.transectsData));
    }

    if (pending.transectsResults && Object.keys(pending.transectsResults).length) {
      setTransectsResults(pending.transectsResults);
      setFinished(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, years]);

  const startCalc = async (baseLineCoords, transectsStep, transectsExtent, threshold, baseline) => {
    try {
      await runCalc(baseLineCoords, transectsStep, transectsExtent, threshold, baseline);
    } catch (error) {
      console.error('[CSqueeze] the transect analysis could not be started', error);
      setAnalyzing(false);
      setCalcMessage(t('forms.csqueeze.result.loading.preparing'));
      notifyError(error, { fallback: t('forms.csqueeze.result.loading.failed') });
    }
  };

  /**
   * @function createTransectObjects
   * @description Draws a transect list on the map and returns the handles the rest of the page
   * works through.
   * @param {Array<Object>} transectsList - `{transect, center, sides}` entries.
   * **Called by:
   * @see {runCalc}
   * @see {the imported-session restore}
   * @memberof module:Csqueeze
   * @returns {Array<Object>} One handle per transect.
   */
  const createTransectObjects = (transectsList) =>
    (transectsList || []).map((transect) => {
      const side1 = transect.sides[0].coords.map(([lng, lat]) => ({ lat, lng }));
      const side2 = transect.sides[1].coords.map(([lng, lat]) => ({ lat, lng }));
      const polyline = new window.google.maps.Polyline({
        path: [...side1, ...side2],
        geodesic: true,
        strokeColor: '#00A391',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: mapRef.current,
      });
      return {
        transect: transect.transect,
        polyline,
        getPath: polyline.getPath.bind(polyline),
        setOptions: polyline.setOptions.bind(polyline),
        setVisible: polyline.setVisible.bind(polyline),
        setMap: polyline.setMap.bind(polyline),
      };
    });

  /**
   * @function runCalc
   * @description The body of {@link startCalc}, kept apart so every way it can fail is caught in
   * one place. Anything thrown in here used to leave `analyzing` true for good: the panel sat on
   * "preparing" with no error, which reads as the analysis having silently stopped.
   * @param {Array<Array<Number>>} baseLineCoords - The drawn baseline, as `[lng, lat]` pairs.
   * @param {Number} transectsStep - Spacing between transects, in metres.
   * @param {Number} transectsExtent - Length of each transect, in metres.
   * @param {Number} threshold - Water/land threshold carried through to the analysis.
   * @param {Object} baseline - The baseline overlay, kept for later redraws.
   * **Called by:
   * @see {startCalc}
   * @memberof module:Csqueeze
   * @returns {Promise<void>}
   */
  const runCalc = async (baseLineCoords, transectsStep, transectsExtent, threshold, baseline) => {
    setBaselineBufferReady(false);
    setAnalyzing(true);
    setCalcMessage(t('forms.csqueeze.result.loading.preparing'));
    await timer(50);
    baselineRef.current = baseline;
    setBaselineCoords(baseLineCoords);
    transectsObjects.forEach((obj) => obj.setMap && obj.setMap(null));
    const newTransectsData = generateTransects(baseLineCoords, transectsStep, transectsExtent);
    setTransectsData(newTransectsData);
    setTransectsObjects(createTransectObjects(newTransectsData));
    let baseLineTurf = turf.lineString(baseLineCoords);
    baselineBufferRef.current = turf.buffer(baseLineTurf, transectsExtent, {
      units: 'meters',
    });
    setBaselineBufferReady(true);
    if (Object.values(classLandCoversVectors).length !== 2 || waterLandCoverVectors.length !== 2) {
      const classifiedImages = Object.values(images || {});
      if (!classifiedImages.length || !coordinates?.length) {
        throw new Error('CSqueeze: no classified images or area available to vectorize.');
      }

      classifiedImages.forEach((image) => {
        dispatch(
          CSqueeze.transformClassToPolygons(
            image.classified,
            image.imageObject.projection(),
            image.date,
            image.type,
            coordinates,
          ),
        );
      });
    }
  };

  /**
   * @function generateTransects
   * @description Generates evenly-spaced transects perpendicular to the drawn baseline
   * using turf.js. For each sample point along the baseline at the given step distance,
   * computes the baseline bearing and creates perpendicular transect sides of the given extent.
   * @param {Array} baseLineCoords - Array of [lng, lat] coordinate pairs for the baseline.
   * @param {number} step - Distance in meters between consecutive transects along the baseline.
   * @param {number} extent - Total length in meters of each transect perpendicular to the baseline.
   * @memberof module:Csqueeze
   * @returns {Array<Object>} Transects: {transect, center, sides: [{side, coords}, {side, coords}]}.
   */
  const generateTransects = (baseLineCoords, step, extent) => {
    const turfLine = turf.lineString(baseLineCoords);
    setTransectsScale(step);
    const lineLength = turf.length(turfLine, { units: 'meters' });
    const points = [];
    for (let d = 0; d <= lineLength; d += step) {
      points.push({ point: turf.along(turfLine, d, { units: 'meters' }), dist: d });
    }
    const outputTransects = [];
    points.forEach(({ point, dist }, i) => {
      if (i === 0) return;
      const pointCoords = point.geometry.coordinates;
      const prevDist = Math.max(0, dist - 1);
      const nextDist = Math.min(lineLength, dist + 1);
      const prevPoint = turf.along(turfLine, prevDist, { units: 'meters' });
      const nextPoint = turf.along(turfLine, nextDist, { units: 'meters' });
      const bearing = turf.bearing(prevPoint, nextPoint);
      const halfExtent = extent / 2;
      const end1 = turf.destination(pointCoords, halfExtent, bearing + 90, { units: 'meters' });
      const end2 = turf.destination(pointCoords, halfExtent, bearing - 90, { units: 'meters' });
      outputTransects.push({
        transect: i,
        center: [pointCoords[1], pointCoords[0]],
        sides: [
          { side: 1, coords: [end1.geometry.coordinates, pointCoords] },
          { side: 2, coords: [pointCoords, end2.geometry.coordinates] },
        ],
      });
    });
    return outputTransects;
  };

  /**
   * @function handlePrevisao
   * @description Updates the forecast horizon (years into the future to project).
   * @param {Event} event - Change event from the forecast input.
   * **Called by:
   * @see {ProcessingPage} - via each forecast-year TextField's onChange.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handlePrevisao = (event) => {
    let value = Math.abs(parseInt(event.target.value));
    if (typeof value !== 'number' || isNaN(value)) return setPrevisao(1);
    if (value > 0) setPrevisao(value);
  };

  /**
   * @function opacityChange
   * @description Sets a GroundOverlay's opacity.
   * @param {Number} level - Opacity level (0-1).
   * @param {google.maps.GroundOverlay} image - Overlay to update.
   * @memberof module:Csqueeze
   * @returns {Number} The applied opacity level.
   */
  const opacityChange = (level, image) => {
    image.setOpacity(level);
    return level;
  };

  /**
   * @function opacityBaseLineChange
   * @description Toggles the drawn baseline's visibility on the map via the baselineRef.
   * @param {boolean} [state] - Desired visibility; toggles the current state if omitted.
   * @memberof module:Csqueeze
   * @returns {boolean} The applied visibility.
   */
  const opacityBaseLineChange = (state) => {
    const baseline = baselineRef.current;
    if (!baseline?.overlay) return state ?? false;
    let visible = state ?? !baseline.overlay.visible;
    baseline.overlay.setVisible(visible);
    return visible;
  };

  /**
   * @function opacityTransectsChange
   * @description Toggles every transect polyline's visibility on the map.
   * @param {boolean} [state] - Desired visibility; toggles the current state if omitted.
   * @memberof module:Csqueeze
   * @returns {boolean} The applied visibility.
   */
  const opacityTransectsChange = (state) => {
    transectsObjects.forEach((transect) => transect.setVisible(state ?? !opacity[2].transects));
    return !opacity[2].transects;
  };

  /**
   * @function exportToCsv
   * @description Triggers a browser download of the given CSV content.
   * @param {String} filename - Download filename.
   * @param {String} csv - CSV content to download.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  function exportToCsv(filename, csv) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * @function handleShapefileExport
   * @description Exports the baseline and transects (enriched with their computed CSV data) as
   * a shapefile group.
   * @see {@link parseCsv}
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const handleShapefileExport = () => {
    try {
      const transectsCsvData = parseCsv(transectsCsv);
      const getTransectDataFromCsv = (index) => transectsCsvData[index - 1];
      const baselineOverlay = baselineRef.current?.overlay;
      const baselinePath = baselineOverlay?.getPath()?.getArray();
      if (!baselinePath) return;
      const baselineShp = {
        features: [
          {
            geometry: {
              type: 'LineString',
              coordinates: baselinePath.map((coord) => [coord.lng(), coord.lat()]),
            },
            properties: {
              name: 'Baseline',
            },
          },
        ],
      };
      const transectsShp = {
        features: transectsData.map((transect) => {
          const csvData = getTransectDataFromCsv(transect.transect);
          return {
            geometry: {
              type: 'LineString',
              coordinates: [...transect.sides[0].coords, ...transect.sides[1].coords.slice().reverse()].map((coord) => [
                coord[0],
                coord[1],
              ]),
            },
            properties: {
              ...csvData,
            },
          };
        }),
      };
      Export.table.toDevice.asShapefileGroup(
        [baselineShp, transectsShp],
        Export.defaultOptions.device.shapefileGroup('layers', 'baseline', 'transects'),
      );
    } catch (error) {
      console.error('Erro ao exportar shapefiles:', error);
    }
  };

  const handleRestartAnalysis = () => {
    dispatch(CSqueeze.resetWaterLandCover());
    dispatch(CSqueeze.resetClassLandCovers());
    setAnalyzing(false);
    baselineRef.current = null;
    setBaselineCoords(null);
    baselineBufferRef.current = [];
    setBaselineBufferReady(false);
    transectsObjects.forEach((transect) => {
      transect.setMap(null);
    });
    if (transectMarker.current?.setMap) transectMarker.current.setMap(null);
    setFinished(false);
    setTransectsResults([]);
    setTransectsData([]);
    setTransectsCsv('');
    setTransectsObjects([]);
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current analysis's configuration (satellite,
   * AOI, years/dates/missions, elevation, training polygons), for `CSqueeze`'s "import session"
   * icon to replay later — a developer/testing shortcut so QA doesn't have to redo the whole
   * wizard every time they need a completed classification to test against.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {ProcessingPage} - via the "export session for testing" button's onClick.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(
      buildSessionExport({
        images,
        years: csqueezeYears,
        dates: csqueezeDates,
        missions: csqueezeMissions,
        coordinates,
        availableDates: acquisitionAvailableDates,
        imageList,
        baseLine: baselineRef.current,
        baseLineCoords: baselineCoords,
        transects: transectsObjects.length,
        transectsData,
        transectsResults,
        transectsCalcs,
        transectsSqueeze,
        forecastYears: previsao,
        transectsScale,
        classLandCoversVectors,
        waterLandCoverVectors,
      }),
      kmlFileName,
    );
  };

  const hasImages = images && Object.keys(images).length > 0;
  useRegisterSessionExport('csqueeze', hasImages ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the analysis's vector layers - area, baseline, transects with their
   * verdict, and each period's classified land cover and water - to a GeoJSON file for a GIS. The
   * periods are written under their labels, so two periods of one year are told apart by the dates
   * they were acquired rather than by a `-1` suffix.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (
      !exportGeoJson({
        coordinates,
        baseLine: baselineRef.current,
        baselineCoords,
        transectsData,
        transectsAttributes: transectsSqueeze,
        classLandCoversVectors,
        waterLandCoverVectors,
        periodLabels,
      })
    ) {
      notifyError(t('forms.acquisition.geoJsonExport.empty'));
    }
  };
  useRegisterGeoJsonExport('csqueeze', hasImages ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildLandCoverReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.csqueeze.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, () => fitCoordinatesWithOverlayPadding(mapRef.current, coordinates));
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildLandCoverReport({
        coordinates,
        baselineCoords,
        transectsData,
        transectsAttributes: transectsSqueeze,
        transectsCalcs,
        periodLabels,
        images: [screen],
        text,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, hasImages ? exportPdfReport : null);


  return (
    <ThemeProvider theme={csqueezeTheme}>
      <Box className={classes.wrapper}>
        <Grid container justifyContent="space-between" id="mapScreen" className={classes.mapOverlay}>
          {' '}
          <Grid
            item
            xs={3}
            className={classes.mapContainer}
            style={{
              flexDirection: 'column',
              justifyContent: 'flex-start',
              maxWidth: '100%',
              maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
            }}>
            <OverlayPanelsToggle side="left" />
            <AnalysisSteps
              title={'forms.csqueeze.result.steps'}
              steps={['DRAW_BASELINE', 'CONFIGURE_TRANSECTS', 'ANALYSIS_DONE']}
              t={t}
              mapRef={mapRef}
              roiCoords={ROI}
              drawingManager={drawingManager}
              onAnalysisPerform={startCalc}
              onRestartAnalysis={handleRestartAnalysis}
            />
            {finished && transectsResults && (
              <Box display="flex" flexDirection="column" style={{ gap: 10, width: '100%' }}>
                <Card style={{ padding: 12 }} elevation={3}>
                  <Typography fontSize={20} style={{ fontWeight: 'bold', marginBottom: 5 }}>
                    {t('forms.csqueeze.result.forecast')}
                  </Typography>
                  <RadioGroup
                    value={String(previsao)}
                    onChange={handlePrevisao}
                    name="forecast-horizon"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                    {[10, 25, 50, 100].map((horizon) => (
                      <FormControlLabel
                        key={horizon}
                        value={String(horizon)}
                        control={<Radio size="small" />}
                        label={<Typography variant="caption">{`${horizon} ${t('forms.csqueeze.years')}`}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                </Card>
                <Card style={{ padding: 12 }} elevation={3}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    marginBottom={2}
                    style={{ gap: 10 }}>
                    <Typography variant="h6">{t('forms.csqueeze.result.transect')}</Typography>
                    <TextField
                      type="number"
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1, max: transectsData.length }}
                      onChange={(e) => setSelectedTransect(e.target.value)}
                      value={selectedTransect}
                      style={{ width: 80, textAlign: 'center' }}
                    />
                  </Box>
                  <TransectResult data={[...(transectsCalcs[selectedTransect] ?? [])]} />
                </Card>
                <Card style={{ padding: 12 }} elevation={3}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Button
                      color="secondary"
                      variant="contained"
                      style={{
                        width: '100%',
                        height: 35,
                        padding: 'inherit 20px',
                        marginBottom: 10,
                      }}
                      onClick={() =>
                        exportToCsv(
                          `cassie_csqueeze_${years.map(periodLabel).map(periodFilenamePart).join('-')}_results_${new Date().toLocaleDateString()}`,
                          transectsCsv,
                        )
                      }>
                      {t('forms.csqueeze.result.export').toUpperCase()}
                    </Button>
                    <Tooltip title={t('forms.csqueeze.result.export')}>
                      <IconButton onClick={() => setShowHelpDialogCSV(true)} size="small" style={{ marginLeft: 8 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SimpleInfoDialog
                    open={showHelpDialogCSV}
                    close={() => setShowHelpDialogCSV(false)}
                    title="forms.csqueeze.result.export"
                    text="forms.csqueeze.result.exportInfo"
                  />
                  <Box display="flex" alignItems="center">
                    <Button
                      color="secondary"
                      variant="contained"
                      style={{
                        width: '100%',
                        height: 35,
                        padding: 'inherit 20px',
                      }}
                      onClick={handleShapefileExport}>
                      {t('forms.csqueeze.result.shp').toUpperCase()}
                    </Button>
                    <Tooltip title={t('forms.csqueeze.result.shp')}>
                      <IconButton onClick={() => setShowHelpDialogShp(true)} size="small" style={{ marginLeft: 8 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SimpleInfoDialog
                    open={showHelpDialogShp}
                    close={() => setShowHelpDialogShp(false)}
                    title="forms.csqueeze.result.shp"
                    text="forms.csqueeze.result.shpInfo"
                  />
                </Card>
              </Box>
            )}
            {analyzing && (
              <Card style={{ padding: 12, width: '100%' }} elevation={3} className={classes.card}>
                <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 10 }}>
                  <CircularProgress size={26} thickness={4.5} color="primary" />
                  <Typography align="center">{calcMessage}</Typography>
                  <Typography variant="caption" align="center">
                    {t('forms.csqueeze.result.loading.wait')}
                  </Typography>
                </Box>
              </Card>
            )}
            {finished && (
              <Card style={{ padding: 12 }} elevation={3} className={classes.card}>
                <Typography fontSize={20} style={{ fontWeight: 'bold', marginBottom: 10 }}>
                  {t('forms.csqueeze.result.colorLegend')} {t('forms.csqueeze.result.transects').toLowerCase()}
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="auto minmax(0, 1fr)"
                  alignItems="center"
                  style={{ gap: '10px', rowGap: '5px' }}>
                  <Box width={30} height={5} style={{ backgroundColor: 'red' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.csqueeze.result.legend.fullSqueeze')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'orange' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.csqueeze.result.legend.moderateSqueeze')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'green' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.csqueeze.result.legend.noSqueeze')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'gray' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.csqueeze.result.legend.noMangrove')}</Typography>
                </Box>
              </Card>
            )}
          </Grid>
          <Grid
            id="side_panel"
            item
            className={classes.mapContainer}
            style={{
              flexDirection: 'column',
              justifyContent: 'flex-start',
              justifySelf: 'end',
              width: OVERLAY_SIDE_COLUMN_WIDTH,
              maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
              paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
              paddingRight: OVERLAY_SIDE_GUTTER,
            }}>
            <OverlayPanelsToggle side="right" />
            <BackgroundMapPanel
              mapType={mapType}
              onMapTypeChange={handleMapTypeChange}
              labelsVisible={labelsVisible}
              onLabelsVisibleChange={setLabelsVisible}
              t={t}
            />
            <Paper className={classes.panelWrapper} elevation={1}>
              <Box className={classes.panelHeader}>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <LayersOutlined fontSize="small" color="primary" />
                  <Typography variant="h6">{t('forms.shorelineAnalysis.layersPanelTitle')}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setLayersPanelOpen((prev) => !prev)}>
                  {layersPanelOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={layersPanelOpen}>
                <Box className={classes.panelContent}>
                  <Box>
                    <Box display="flex" alignItems="center" gridGap={4}>
                      <Typography style={{ fontWeight: 'bold' }}>
                        {t('forms.csqueeze.result.visibilityController')}
                      </Typography>
                      <Tooltip title={t('forms.csqueeze.result.visibilityController')}>
                        <IconButton
                          onClick={() => setShowHelpDialogVisibility(true)}
                          size="small"
                          style={{ padding: 4 }}>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <SimpleInfoDialog
                      open={showHelpDialogVisibility}
                      close={() => setShowHelpDialogVisibility(false)}
                      title="forms.csqueeze.result.visibilityController"
                      text="forms.csqueeze.result.visibilityControllerinfo"
                    />
                    <Typography variant="caption">{t('forms.csqueeze.result.visibilityControllerDesc')}</Typography>
                  </Box>
                  {Object.values(images)
                    .reverse()
                    .map((image, index) => (
                      <Box key={index} className={classes.panelSection}>
                        <div
                          style={{
                            fontWeight: 'bold',
                            borderBottom: '1px solid gray',
                            paddingBottom: 5,
                            marginBottom: 5,
                          }}>
                          {periodLabel(image.name)} ({image.missionName})
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            padding: '10px 0',
                          }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '90px minmax(0, 1fr) auto auto',
                              gap: 10,
                            }}>
                            <div>Raster</div>
                            <Slider
                              key={opacity[index].raster > 0 ? 1 : 0}
                              value={opacity[index].raster}
                              step={0.05}
                              min={0.0}
                              max={1.0}
                              onChange={(_, value) => {
                                let level = opacityChange(value, image.classification);
                                setOpacity({
                                  ...opacity,
                                  [index]: { ...opacity[index], raster: level },
                                });
                              }}
                              aria-labelledby="continuous-slider"
                            />
                            <IconButton
                              onClick={() => {
                                let level = opacityChange(image.classification.opacity ? 0 : 1, image.classification);
                                setOpacity({
                                  ...opacity,
                                  [index]: { ...opacity[index], raster: level },
                                });
                              }}
                              style={{
                                marginLeft: 14,
                                width: 10,
                                height: 10,
                              }}>
                              {opacity[index].raster > 0 ? (
                                <VisibilityIcon fontSize="large" color="primary" />
                              ) : (
                                <VisibilityOffIcon fontSize="large" />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={() => window.open(image.classification.url, 'download')}
                              style={{
                                width: 10,
                                height: 10,
                              }}>
                              <GetAppIcon fontSize="large" />
                            </IconButton>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '90px minmax(0, 1fr) auto auto',
                              gap: 10,
                            }}>
                            <div>Satellite image</div>
                            <Slider
                              key={opacity[index].overlay > 0 ? 1 : 0}
                              value={opacity[index].overlay}
                              step={0.05}
                              min={0.0}
                              max={1.0}
                              onChange={(_, value) => {
                                let level = opacityChange(value, image.overlay);
                                setOpacity({
                                  ...opacity,
                                  [index]: { ...opacity[index], overlay: level },
                                });
                              }}
                              aria-labelledby="non-linear-slider"
                            />
                            <IconButton
                              onClick={() => {
                                let level = opacityChange(image.overlay.opacity ? 0 : 1, image.overlay);
                                setOpacity({
                                  ...opacity,
                                  [index]: { ...opacity[index], overlay: level },
                                });
                              }}
                              style={{
                                marginLeft: 14,
                                width: 10,
                                height: 10,
                              }}>
                              {opacity[index].overlay > 0 ? (
                                <VisibilityIcon fontSize="large" color="primary" />
                              ) : (
                                <VisibilityOffIcon fontSize="large" />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={() => window.open(image.url, 'download')}
                              style={{
                                width: 10,
                                height: 10,
                              }}>
                              <GetAppIcon fontSize="large" />
                            </IconButton>
                          </div>
                        </div>
                      </Box>
                    ))}
                  {analyzing && (
                    <Box className={classes.panelSection}>
                      <div
                        style={{
                          fontWeight: 'bold',
                          borderBottom: '1px solid gray',
                          paddingBottom: 5,
                          marginBottom: 5,
                        }}>
                        {t('forms.csqueeze.result.polygons')}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          padding: '10px 0',
                        }}>
                        {transectsObjects.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 10,
                            }}>
                            <div>{t('forms.csqueeze.result.transects')}</div>
                            <IconButton
                              onClick={() => {
                                let level = opacityTransectsChange();
                                setOpacity({
                                  ...opacity,
                                  2: { ...opacity[2], transects: level },
                                });
                              }}
                              style={{
                                marginLeft: 14,
                                width: 10,
                                height: 10,
                              }}>
                              {opacity[2].transects ? (
                                <VisibilityIcon fontSize="large" color="primary" />
                              ) : (
                                <VisibilityOffIcon fontSize="large" />
                              )}
                            </IconButton>
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}>
                          <div>{t('forms.csqueeze.result.baseLine')}</div>
                          <IconButton
                            onClick={() => {
                              let level = opacityBaseLineChange();
                              setOpacity({
                                ...opacity,
                                2: { ...opacity[2], baseline: level },
                              });
                            }}
                            style={{
                              marginLeft: 14,
                              width: 10,
                              height: 10,
                            }}>
                            {opacity[2].baseline ? (
                              <VisibilityIcon fontSize="large" color="primary" />
                            ) : (
                              <VisibilityOffIcon fontSize="large" />
                            )}
                          </IconButton>
                        </div>
                      </div>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
        <ReturnButtons lastStepPath="/main/csqueeze/5" />
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
            setMapReady(true);
            map.setMapTypeId(mapType);
            displayROI(map);
            transectMarker.current = new window.google.maps.Marker({
              map: map,
              visible: false,
              label: '',
            });
            applyMapThemeStyle();
            window.google.maps.event.addListener(map, 'maptypeid_changed', applyMapThemeStyle);
          }}
          onBoundsChanged={(bounds) => {
            boundsRef.current = bounds;
          }}
          style={{ position: 'absolute' }}
          mapOptions={{ mapTypeControl: false }}
        />
      </Box>
    </ThemeProvider>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * CSqueeze processing page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Csqueeze
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexFlow: 'column',
    paddingTop: OVERLAY_COLUMN_PADDING_TOP,
    paddingBottom: 10,
    paddingLeft: OVERLAY_SIDE_GUTTER,
    paddingRight: OVERLAY_SCROLLBAR_GUTTER,
    gap: 10,
    overflowY: 'auto',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  mapOverlay: {
    display: 'grid',
    width: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 10,
    gridTemplateColumns: `${OVERLAY_MAIN_COLUMN_WIDTH}px 1fr`,
    gridTemplateRows: '100%',
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  panelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: OVERLAY_PANEL_GAP,
    padding: 12,
  },
  panelSection: {
    paddingTop: OVERLAY_PANEL_GAP,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  backgroundMapPanel: {
    padding: theme.spacing(1, 1.5),
    maxWidth: 180,
  },
  card: {
    height: 'fit-content !important',
    overflow: 'unset !important',
    '& .MuiCard-root': {
      overflow: 'unset !important',
    },
  },
}));

export default ProcessingPage;
